import { Injectable } from "@angular/core";
import { ContentType } from "../shared/contentTypes";
import Utils from "../shared/utils";
import { LikeService } from "./like.service";
import { CircularInjectionHelper } from "./circularInjectionHelper";
import { SetCustomerArtikelTagModel, SetCustomerArtikelTagsSuccessModel, UserInfoData } from "../shared/models/userInfo.model";
import { Observable, BehaviorSubject } from "rxjs";
import { StorageHive, LocalKeys } from "./valueStorage.service";

@Injectable({
  providedIn: "root"
})
export class UserHelperService {
  /** member für IsRegistered und RegisterObserver, nur über SetIsRegistered ändern */
  private _isRegistered: boolean = false;
  private _isTestUser: boolean = false;
  private _registerSubject = new BehaviorSubject<boolean>(false);

  public get IsRegistered(): boolean {
    return this._isRegistered;
  }
  /** ändert den IsRegistered und löst bei bedarf RegisterObserver aus */
  private SetIsRegistered(value: boolean): void {
    if (value != this._isRegistered) {
      this._isRegistered = value;
      this._registerSubject.next(this._isRegistered);
    } // else no change
  }

  public get IsTestUser(): boolean {
    return this._isTestUser;
  }

  public get RegisterObserver(): Observable<boolean> {
    return this._registerSubject;
  }

  constructor(private _likeService: LikeService,
    private _circularInjectionHelper: CircularInjectionHelper) {

  }

  /**
   * check if we have a user and user is registered
   * if user has name and at least one location it is registered
   * @needLocation if it is need to check like location count; must be true in registrationData Page
   */
  public IsRegistrationConfirmed(needLocation: boolean = false, forceUserInfo: boolean = false): Promise<boolean> {
    const me = this;
    return new Promise<boolean>((resolve, reject) => {
      me._circularInjectionHelper.yourAppServer.User.GetUserInfo(forceUserInfo)
        .then((userInfo) => {
          if (!userInfo) {
            me.SetIsRegistered(false);
            resolve(false);
            return;
          }
          if (needLocation) {
            me._likeService.GetContentIDForActiveLikes(ContentType.Location)
              .then((locationIDs) => {
                if (Utils.IsNull(userInfo.fname) || userInfo.fname.length === 0
                  || Utils.IsNull(locationIDs) || locationIDs.length === 0) {
                  me.SetIsRegistered(false);
                  me._isTestUser = false;
                  resolve(false);
                } else {
                  me.SetIsRegistered(true);
                  me._isTestUser = me.checkTestUser(userInfo);
                  resolve(true);
                }
              }).catch((e) => {
                me._circularInjectionHelper.coreLogService.error("UserHelperService.IsRegistrationConfirmed", "_likeService.GetContentIDForActiveLikes()", e);
                reject();
              });
          } else {
            if (Utils.IsNull(userInfo.fname) || userInfo.fname.length === 0) {
              me.SetIsRegistered(false);
              me._isTestUser = false;
              resolve(false);
            } else {
              me.SetIsRegistered(true);
              me._isTestUser = me.checkTestUser(userInfo);
              resolve(true);
            }
          }
        })
        .catch((e) => {
          me._circularInjectionHelper.coreLogService.error("UserHelperService.IsRegistrationConfirmed", "_circularInjectionHelper.yourAppServer.User.GetUserInfo()", e);
          reject();
        });
    });
  }

  public HasWebVoucher(): Promise<boolean> {
    const me = this;
    return new Promise<boolean>((resolve, reject) => {
      me.GetUserData(true)
        .then((response) => {
          if (response && response.card_qr_code_url !== "") {
            resolve(true);
          } else {
            resolve(false);
          }
        })
        .catch((error) => {
          me._circularInjectionHelper.coreLogService.error("UserHelperService.HasWebVoucher", "me.GetUserData", error);
          reject();
        });
    });
  }

  /** Gets UserData, if need or forced from server, else load from Storage */
  public GetUserData(force: boolean): Promise<UserInfoData> {
    const me = this;
    return new Promise<UserInfoData>((resolve, reject) => {
      if (me._circularInjectionHelper.yourAppServer.coreCOMService.environment.networkInformation.isOnline) {
        me._circularInjectionHelper.yourAppServer.User.GetUserInfo(force)
          .then(resolve)
          .catch((error) => {
            me._circularInjectionHelper.coreLogService.error("UserHelperService.GetUserData", "_circularInjectionHelper.yourAppServer.User.GetUserInfo()", error);
            reject();
          });
      } else {
        me._circularInjectionHelper.yourAppServer.coreCOMService.environment.valueStorage.get(StorageHive.Local, LocalKeys.yourAppUserData)
          .then(resolve)
          .catch((error) => {
            me._circularInjectionHelper.coreLogService.error("WebvoucherGuard.GetUserData", "valueStorage.get LocalKeys.yourAppUserData", error);
            reject();
          });
      }
    });
  }

  /** Sets the liked and disliked tags for current customer */
  public SetCustomerArtikelTags(tags_liked: number[], tags_disliked: number[]): Promise<SetCustomerArtikelTagsSuccessModel> {
    const me = this;
    return new Promise<SetCustomerArtikelTagsSuccessModel>((resolve, reject) => {
      try {
        const RequestData = new SetCustomerArtikelTagModel();
        RequestData.tags_liked = tags_liked;
        RequestData.tags_disliked = tags_disliked;
        const FunctionName = "account/SetCustomerArtikelTags";
        me._circularInjectionHelper.yourAppServer.Api.RequestService(SetCustomerArtikelTagsSuccessModel, FunctionName, RequestData)
          .then((response) => {
            if (response.OK) {
              me._circularInjectionHelper.coreLogService.debug("UserHelperService.SetCustomerArtikelTags", "RequestService (response OK)", response);
              resolve(response.Data);
            } else {
              me._circularInjectionHelper.coreLogService.error("UserHelperService.SetCustomerArtikelTags", "RequestService (response not OK)", response);
              reject(response.HttpResponse.codeText);
            }
          })
          .catch((error) => { me._circularInjectionHelper.coreLogService.error("UserHelperService.SetCustomerArtikelTags", "RequestService", error); reject(); });
      } catch (error) {
        me._circularInjectionHelper.coreLogService.error("UserHelperService.SetCustomerArtikelTags", "try catch", error);
        reject();
      }
    });
  }

  /**
   * check if user is a testUser
   * if is_testuser property of a user model is true the user is a testUser
   */
  private checkTestUser(userInfo: UserInfoData): boolean {
    if (!Utils.IsNull(userInfo) && userInfo.is_testuser) {
      return true;
    } else {
      return false;
    }
  }

  public clearHelperDataService() {
    const me = this;
    me.SetIsRegistered(false);
    me._isTestUser = false;
  }
}
